import { getAuth } from "firebase/auth";
import { apiUrl, apiUrlDev, apiUrlLocal } from "../../globalVariables";

// Build the list of base URLs to try, mirroring the pattern used by
// LangfuseAnalytics / LeadConversion: on localhost we fall back through
// local emulator → dev project → prod, otherwise we just hit prod.
const getBaseUrls = () => {
  const isLocal =
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1");
  return isLocal ? [apiUrlLocal, apiUrlDev, apiUrl] : [apiUrl];
};

const getUserId = () => {
  const userId = getAuth()?.currentUser?.uid;
  if (!userId) {
    throw new Error("Not authenticated. Please refresh and sign in again.");
  }
  return userId;
};

const buildUrl = (baseUrl, path, params = {}) => {
  // baseUrl already ends in "/admin" (cloud function name). Inside the
  // express app we mount a second "/admin" prefix, so callers pass the path
  // as e.g. "/support-insights/overview" and we tack "/admin" in front.
  const url = new URL(`${baseUrl}/admin${path}`);
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      url.searchParams.set(key, value);
    }
  });
  return url.toString();
};

const fetchWithFallback = async (path, params = {}, { timeoutMs = 60000 } = {}) => {
  const userId = getUserId();
  const baseUrls = getBaseUrls();
  let lastError = "Request failed";

  for (const baseUrl of baseUrls) {
    try {
      const url = buildUrl(baseUrl, path, { ...params, userId });
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), timeoutMs);
      const res = await fetch(url, { signal: controller.signal });
      clearTimeout(timer);
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        lastError = `HTTP ${res.status} from ${baseUrl}: ${text.slice(0, 200)}`;
        continue;
      }
      const json = await res.json();
      if (json && json.success === false) {
        lastError = json.error || json.reason || "API returned success=false";
        continue;
      }
      return json;
    } catch (err) {
      lastError = err?.message || String(err);
    }
  }

  throw new Error(lastError);
};

export const fetchOverview = (days = 90) =>
  fetchWithFallback("/support-insights/overview", { days });

export const fetchTopics = ({ bucket = "weekly", days = 90 } = {}) =>
  fetchWithFallback("/support-insights/topics", { bucket, days });

export const fetchCategoryDetail = (
  categoryId,
  { bucket = "weekly", days = 180, limit = 25, offset = 0, tag = null } = {},
) =>
  fetchWithFallback(`/support-insights/category/${encodeURIComponent(categoryId)}`, {
    bucket,
    days,
    limit,
    offset,
    tag,
  });

export const fetchTaxonomy = () => fetchWithFallback("/support-insights/taxonomy");

// ---------------------------------------------------------------------------
// Help Center article automation
// ---------------------------------------------------------------------------

const postWithFallback = async (path, body = {}, { timeoutMs = 60000 } = {}) => {
  const userId = getUserId();
  const baseUrls = getBaseUrls();
  let lastError = "Request failed";

  for (const baseUrl of baseUrls) {
    try {
      const url = buildUrl(baseUrl, path);
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), timeoutMs);
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...body, userId }),
        signal: controller.signal,
      });
      clearTimeout(timer);
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        lastError = `HTTP ${res.status} from ${baseUrl}: ${text.slice(0, 200)}`;
        continue;
      }
      const json = await res.json();
      if (json && json.success === false) {
        lastError = json.error || json.reason || "API returned success=false";
        continue;
      }
      return json;
    } catch (err) {
      lastError = err?.message || String(err);
    }
  }

  throw new Error(lastError);
};

/**
 * Manually start a help-center article job for a topic. No ticket-count
 * threshold applies to manual starts.
 * @param {{ topicPhrase: string, categoryId?: string, linkedTickets?: string[] }} params
 */
export const createHelpDocJob = ({ topicPhrase, categoryId, linkedTickets } = {}) =>
  postWithFallback("/support-insights/help-doc-jobs", {
    topicPhrase,
    categoryId,
    linkedTickets,
  });

export const fetchHelpDocJobs = (limit = 50) =>
  fetchWithFallback("/support-insights/help-doc-jobs", { limit });
